import "./styles.scss";

import Parser from "@helpers/parser";
import { Text, Button, Icon } from "@fragments";
import { CommunityBanner } from "@components/Community";
import {
  IconArrowRight,
  IconPhoto,
  IconSchool,
  IconPercentage,
  IconClockHour4,
  IconCoin,
  IconFileText,
  IconFileDescription,
  IconBuildingCommunity,
  IconTimeline,
  IconClipboardText,
  IconCalendarEvent,
  IconFlag,
} from "@tabler/icons-react";

import { DURATION_UNIT_LABEL } from "@pages/CommunityConfig/tabs/Mentoring/enums/durationUnits";
import { formatTimelineDate } from "@pages/Mentoring/helpers/mentoringTimeline";
import MentoringStatusChip from "../../components/MentoringStatusChip";
import MentoringTimeline from "@pages/Mentoring/components/MentoringTimeline";
import RevenueSection from "./components/RevenueSection";

/**
 * Creator — single subscribed mentoring (§6.11 detail). Hero (program identity +
 * dates) → status callouts → community revenue (insights link + chart) → a
 * two-column layout (sobre o programa · linha do tempo / comunidade).
 */
export default function MyMentoringDetailView({
  enrollment,
  loading,
  notFound,
  onAttach,
  revenue,
  onOpenInsights,
}) {
  if (loading) {
    return (
      <div className="creator-mentoring px-4">
        <div className="cm-hero skel" />
        <div className="cm-grid">
          <div className="cm-skeleton skel" style={{ height: 220 }} />
          <div className="cm-skeleton skel" style={{ height: 220 }} />
        </div>
      </div>
    );
  }

  if (notFound || !enrollment) {
    return (
      <div className="creator-mentoring px-4">
        <div className="cm-empty">
          <Icon.Sphere size={72}>
            <IconSchool />
          </Icon.Sphere>
          <Text.h4 contrast={6}>Mentoria não encontrada</Text.h4>
          <Text.span contrast={5}>
            Esta mentoria pode ter sido encerrada ou o link não é mais válido.
          </Text.span>
        </div>
      </div>
    );
  }

  const { program, community, status, timestamps = {} } = enrollment;
  const splitPct =
    enrollment.effective_split_percentage ?? program?.split_percentage;
  const startLabel = timestamps.started_at
    ? formatTimelineDate(timestamps.started_at)
    : "";
  const endLabel = timestamps.ends_at
    ? formatTimelineDate(timestamps.ends_at)
    : "";

  return (
    <div className="creator-mentoring px-4 pt-5">
      {/* ── Hero: 4:3 banner (same ratio as the cards) + program identity ──── */}
      <div className="cm-hero">
        <div className="cm-hero-media">
          {program?.banner?.url ? (
            <img src={program.banner.url} alt={program.name} loading="lazy" />
          ) : (
            <div className="cm-hero-media-fallback">
              <IconPhoto size={30} />
            </div>
          )}
        </div>
        <div className="cm-hero-info">
          <MentoringStatusChip status={status} />
          <Text.h1 weight={500} maxLines={3}>
            {program?.name}
          </Text.h1>
          {program?.description && (
            <Text.p contrast={6} maxLines={3} className="cm-hero-desc">
              {program.description}
            </Text.p>
          )}
          {(startLabel || endLabel) && (
            <div className="cm-hero-meta">
              {startLabel && (
                <Text.span contrast={6} left={<IconCalendarEvent size={14} />}>
                  Início {startLabel}
                </Text.span>
              )}
              {endLabel && (
                <Text.span contrast={6} left={<IconFlag size={14} />}>
                  Término {endLabel}
                </Text.span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Status callouts ─────────────────────────────────────────────── */}
      {status === "awaiting_community" && (
        <div className="cm-callout cm-callout--action">
          <div className="cm-callout-main">
            <span className="cm-callout-icon">
              <IconBuildingCommunity size={18} />
            </span>
            <div className="cm-callout-text">
              <Text.h5 contrast={9} weight={500}>
                Vincule sua comunidade para ativar a mentoria
              </Text.h5>
              <Text.span contrast={6}>
                {enrollment.next_steps ??
                  "Escolha a comunidade que receberá a mentoria e o repasse de receita."}
              </Text.span>
            </div>
          </div>
          <Button
            text="Vincular comunidade"
            endIcon={<IconArrowRight />}
            color="primary"
            size="small"
            onClick={onAttach}
            preventLoading
          />
        </div>
      )}

      {status === "pending" && enrollment.next_steps && (
        <div className="cm-callout cm-callout--info">
          <IconClipboardText size={18} />
          <Text.span contrast={8}>{enrollment.next_steps}</Text.span>
        </div>
      )}

      {status === "split_only" && (
        <div className="cm-callout cm-callout--info">
          <IconCoin size={18} />
          <Text.span contrast={8}>
            Mentoria concluída — repasse ativo até{" "}
            {fmtReadable(timestamps.split_ends_at)}.
          </Text.span>
        </div>
      )}

      {/* ── Sobre o programa  |  Linha do tempo / Comunidade ────────────── */}
      <div className="cm-grid">
        <div className="cm-col">
          <Card
            title="Sobre o programa"
            icon={<IconFileDescription size={16} />}
          >
            <Row
              label="Repasse de receita"
              icon={<IconPercentage size={14} />}
              value={splitPct != null ? `${splitPct}%` : "—"}
              emphasis
            />
            <Row
              label="Duração da mentoria"
              icon={<IconClockHour4 size={14} />}
              value={
                program?.duration_value
                  ? `${program.duration_value} ${DURATION_UNIT_LABEL[program.duration_unit] ?? ""}`.trim()
                  : "Indefinida"
              }
            />
            <Row
              label="Janela de repasse"
              icon={<IconCoin size={14} />}
              value={
                program?.split_duration_value
                  ? `${program.split_duration_value} ${DURATION_UNIT_LABEL[program.split_duration_unit] ?? ""}`.trim()
                  : "= duração da mentoria"
              }
            />
            {program?.contract_url && (
              <Row
                label="Contrato"
                icon={<IconFileText size={14} />}
                value={
                  <a
                    className="cm-link"
                    href={program.contract_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Text.span left={<IconFileText size={14} />} contrast={7}>
                      Ver contrato
                    </Text.span>
                  </a>
                }
              />
            )}
          </Card>
          {/* ── Community revenue: insights link + monthly chart ────────────── */}
          {revenue?.available && (
            <RevenueSection
              revenue={revenue.series}
              loading={revenue.loading}
              communityName={revenue.communityName}
              startDateLabel={revenue.startDateLabel}
              referenceDate={revenue.referenceDate}
              onOpenInsights={onOpenInsights}
            />
          )}
        </div>

        <div className="cm-col">
          <Card title="Linha do tempo" icon={<IconTimeline size={16} />}>
            <MentoringTimeline timestamps={timestamps} />
            {enrollment.notes && (
              <div className="cm-notes">
                <Text.span contrast={5}>Observações</Text.span>
                <Text.p contrast={7}>{enrollment.notes}</Text.p>
              </div>
            )}
          </Card>

          {community && (
            <Card
              title="Comunidade vinculada"
              icon={<IconBuildingCommunity size={16} />}
              className="cm-card--soft"
            >
              <div className="cm-community">
                <CommunityBanner banner={community.banner_url} size={56} />
                <div className="cm-community-info">
                  <Text.h5 contrast={7} weight={500} maxLines={1}>
                    {community.name}
                  </Text.h5>
                  <Text.span contrast={5}>Recebendo a mentoria</Text.span>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Card({ title, icon, className = "", children }) {
  return (
    <div className={`cm-card${className ? ` ${className}` : ""}`}>
      <div className="cm-card-head">
        {icon && <span className="cm-card-icon">{icon}</span>}
        <Text.h5 contrast={8} weight={500}>
          {title}
        </Text.h5>
      </div>
      <div className="cm-card-body">{children}</div>
    </div>
  );
}

function Row({ label, value, icon, emphasis }) {
  return (
    <div className="cm-row">
      <Text.span contrast={5} left={icon}>
        {label}
      </Text.span>
      {typeof value === "string" ? (
        <Text.span contrast={emphasis ? 9 : 8} weight={emphasis ? 500 : 300}>
          {value}
        </Text.span>
      ) : (
        value
      )}
    </div>
  );
}

function fmtReadable(iso) {
  return iso ? Parser.date.readable(iso) : "—";
}
